import { navigate } from "@reach/router"
import { Grid, GridCell } from "@shared/primitive-components"
import { formatPercentage } from "@shared/utils/formatters/formatPercentage"
import { StatCard } from "."
import { getFeedbackProps } from ".."
import { PATHS } from "../../../const/paths"
import { rebatesStyles as styles } from "../../styles"

export const STAT_LIST_DTIS = {
  MILEAGE: {
    LEARN_MORE: `mileage_learn_more`,
    STAT: `mileage_stat`,
    LABEL: `mileage_label`,
    FEEDBACK: {
      ICON: `mileage_feedback_icon`,
      TEXT: `mileage_feedback_text`,
    },
  },
  RISK_SCORE: {
    LEARN_MORE: `safety_score_learn_more`,
    STAT: `risk_score_stat`,
    LABEL: `risk_score_label`,
    FEEDBACK: {
      ICON: `risk_score_feedback_icon`,
      TEXT: `risk_score_feedback_text`,
    },
  },
  LOSS_RATIO: {
    LEARN_MORE: `loss_ratio_learn_more`,
    STAT: `loss_ratio_stat`,
    LABEL: `loss_ratio_label`,
    FEEDBACK: {
      ICON: `loss_ratio_feedback_icon`,
      TEXT: `loss_ratio_feedback_text`,
    },
  },
}

type StatProps = {
  value: number | null
  threshold: number
}

export type StatListProps = {
  mileage: StatProps
  lossRatio: StatProps
  fleetScore: StatProps
  providerLoading?: boolean
}

const hasData = (input: number | null | unknown): input is number => typeof input === `number`

export const StatList = ({ providerLoading, mileage, lossRatio, fleetScore }: StatListProps) => {
  const LossRatioStatCard = (
    <GridCell span={4}>
      <StatCard
        providerLoading={providerLoading}
        stat={hasData(lossRatio.value) ? formatPercentage(lossRatio.value ? lossRatio.value : 0, 1, false) : `N/A`}
        statLabel={hasData(lossRatio.value) ? `%` : ``}
        target={hasData(lossRatio.threshold) ? `Target: Below ${lossRatio.threshold}%` : `Target: no data`}
        title="Loss Ratio"
        tooltipText="In order to be eligible to collect your accumulated rebate you must be below the threshold at the end of your policy period."
        button={{
          action: () => navigate(PATHS.CLAIMS),
          text: `Learn more`,
        }}
        feedback={getFeedbackProps(
          hasData(lossRatio.value) && hasData(lossRatio.threshold),
          (lossRatio.value ?? 0) * 100 < lossRatio.threshold,
          `You're ${(lossRatio.value ?? 0) * 100 < lossRatio.threshold ? `` : `not currently`} eligible for a rebate`
        )}
        dataTestIds={{
          learnMore: STAT_LIST_DTIS.LOSS_RATIO.LEARN_MORE,
          stat: STAT_LIST_DTIS.LOSS_RATIO.STAT,
          label: STAT_LIST_DTIS.LOSS_RATIO.LABEL,
          feedback: {
            icon: STAT_LIST_DTIS.LOSS_RATIO.FEEDBACK.ICON,
            text: STAT_LIST_DTIS.LOSS_RATIO.FEEDBACK.TEXT,
          },
        }}
      />
    </GridCell>
  )

  const SafetyScoreStatCard = (
    <GridCell span={4}>
      <StatCard
        providerLoading={providerLoading}
        stat={hasData(fleetScore.value) ? Math.round(fleetScore.value).toString() : `N/A`}
        statLabel={hasData(fleetScore.value) ? `/100` : ``}
        target={`Starting Safety Score: ${
          hasData(fleetScore.threshold) ? `${Math.round(fleetScore.threshold)}` : `no data`
        }`}
        title="Safety Score"
        tooltipText="Accumulate a rebate of up to 4% of your total premium by increasing your Safety Score above Starting Safety Score during the policy period. Please note that the Safety Score displayed here is rounded to the nearest whole number."
        button={{
          action: () => navigate(PATHS.INSIGHTS.BASE),
          text: `Learn more`,
        }}
        feedback={getFeedbackProps(
          hasData(fleetScore.value) && hasData(fleetScore.threshold),
          (fleetScore.value ?? 0) > fleetScore.threshold,
          `Your Safety Score is ${
            (fleetScore.value ?? 0) < fleetScore.threshold ? `lower` : `higher`
          } than your Starting Safety Score`
        )}
        dataTestIds={{
          learnMore: STAT_LIST_DTIS.RISK_SCORE.LEARN_MORE,
          stat: STAT_LIST_DTIS.RISK_SCORE.STAT,
          label: STAT_LIST_DTIS.RISK_SCORE.LABEL,
          feedback: {
            icon: STAT_LIST_DTIS.RISK_SCORE.FEEDBACK.ICON,
            text: STAT_LIST_DTIS.RISK_SCORE.FEEDBACK.TEXT,
          },
        }}
      />
    </GridCell>
  )

  const MileageStatCard = (
    <GridCell span={4}>
      <StatCard
        providerLoading={providerLoading}
        stat={hasData(mileage.value) ? mileage.value.toLocaleString() : `N/A`}
        statLabel={hasData(mileage.value) ? `mi` : ``}
        lowercaseLabel
        target={hasData(mileage.threshold) ? `Estimate: ${mileage.threshold.toLocaleString()}mi` : `Estimate: no data`}
        title="Mileage"
        tooltipText="Accumulate a rebate of up to 6% of your total premium by driving less than your estimated mileage during the policy period."
        button={{
          action: () => navigate(PATHS.MILEAGE),
          text: `Learn more`,
        }}
        feedback={getFeedbackProps(
          hasData(mileage.value) && hasData(mileage.threshold),
          (mileage.value ?? 0) < mileage.threshold,
          `Your mileage is ${(mileage.value ?? 0) < mileage.threshold ? `lower` : `higher`} than estimated`
        )}
        dataTestIds={{
          learnMore: STAT_LIST_DTIS.MILEAGE.LEARN_MORE,
          stat: STAT_LIST_DTIS.MILEAGE.STAT,
          label: STAT_LIST_DTIS.MILEAGE.LABEL,
          feedback: {
            icon: STAT_LIST_DTIS.MILEAGE.FEEDBACK.ICON,
            text: STAT_LIST_DTIS.MILEAGE.FEEDBACK.TEXT,
          },
        }}
      />
    </GridCell>
  )

  return (
    <Grid css={styles.gridWrap}>
      {SafetyScoreStatCard}
      {LossRatioStatCard}
      {MileageStatCard}
    </Grid>
  )
}

StatList.defaultProps = {
  providerLoading: undefined,
}
